import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, Phone, Mail, MapPin, Clock } from "lucide-react";
import MaroonNavbar from "@/components/new/MaroonNavbar";
import MaroonFooter from "@/components/new/MaroonFooter";
import WhatsAppButton from "@/components/new/WhatsAppButton";

const C = { maroon: "#621B21", maroonDark: "#5C0F1C", yellow: "#F5C800", beige: "#F5EDD6", white: "#FFFFFF" };
const LAB_IMG = "https://images.pexels.com/photos/9574465/pexels-photo-9574465.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DOG_HELD = "https://images.unsplash.com/photo-1510524397145-0d2ab89974d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxjdXRlJTIwZG9nJTIwYmVpbmclMjBoZWxkJTIwZ2VudGx5JTIwd2FybSUyMGxpZ2h0fGVufDB8fHx8MTc3NjY3NjU0Nnww&ixlib=rb-4.1.0&q=85";
const DOG_RUN = "https://images.unsplash.com/photo-1768084368558-0c4f68278309?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxnb2xkZW4lMjByZXRyaWV2ZXIlMjBydW5uaW5nJTIwaGFwcHklMjBoZWFsdGh5JTIwb3V0ZG9vcnxlbnwwfHx8fDE3NzY2NzY1Mzd8MA&ixlib=rb-4.1.0&q=85";
const LAB2_IMG = "https://images.pexels.com/photos/9485592/pexels-photo-9485592.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DOG_OWNER = "https://images.unsplash.com/photo-1662987610442-2f9c4a90a6dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGRvZyUyMG93bmVyJTIwd2FybSUyMHBvcnRyYWl0JTIwdG9nZXRoZXJ8ZW58MHx8fHwxNzc2Njc2NTM3fDA&ixlib=rb-4.1.0&q=85";

const useReveal = () => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => { const el = ref.current; if (!el) return; const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.15 }); o.observe(el); return () => o.disconnect(); }, []);
  return [ref, vis];
};

/* ─── Our Story ─── */
const OurStorySection = () => {
  const [ref, vis] = useReveal();
  return (
    <section ref={ref} className="pt-24 md:pt-28 pb-16 md:pb-24" style={{ background: C.maroon }} data-testid="about-story">
      <div className="max-w-[1200px] mx-auto px-6 md:px-[52px]">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div><img src={LAB_IMG} alt="Lab" className="w-full h-[300px] md:h-[480px] object-cover rounded-2xl shadow-2xl opacity-90" /></div>
          <div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>Our Story.</h1>
            <p className="text-sm md:text-base leading-relaxed mb-6" style={{ color: `${C.beige}CC` }}>
              ETHIA was born out of one real question — a question that changed everything. We have fought hard to make human food clean, ethical and transparent. Free-range. Organic. Cruelty-free. We read every label. We demand better.
            </p>
            <div className="pl-4 mb-6" style={{ borderLeft: `3px solid ${C.yellow}` }}>
              <p className="text-lg md:text-xl font-bold italic" style={{ color: C.yellow, fontFamily: "Fraunces, serif" }}>"But what about our pets?"</p>
            </div>
            <p className="text-sm md:text-base leading-relaxed mb-6" style={{ color: `${C.beige}CC` }}>
              Every year, millions of animals are slaughtered in factory farms — just to fill a pet food pouch. Low-quality meat byproducts. Fillers. Artificial additives. No transparency. Our pets deserve exactly what we demand for ourselves.
            </p>
            <div className="rounded-xl p-4" style={{ background: `${C.yellow}15`, border: `1px solid ${C.yellow}30` }}>
              <p className="text-sm font-bold" style={{ color: C.yellow }}>That is why we created ETHIA. Cultivated pet food made with real chicken cells. 100% slaughter-free. 100% nutritious. 100% planet-friendly.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── Five Pillars ─── */
const PILLARS = [
  { letter: "E", title: "Ethical by Design", img: DOG_HELD, desc: "Beyond zero cruelty. We have removed the moral weight of pet ownership by providing real meat that requires zero slaughter. Your dog's dinner no longer costs another animal its life." },
  { letter: "T", title: "Transparent Technology", img: LAB_IMG, desc: "We have replaced unknown sourcing with a protected, sterile cultivation process. You know exactly where every gram of protein comes from. No mystery. No derivatives. No compromise." },
  { letter: "H", title: "Health Without Compromise", img: DOG_RUN, desc: "100% real chicken protein formulated for high bioavailability and consistent energy." },
  { letter: "I", title: "Innovation for Safety", img: LAB2_IMG, desc: "By growing protein in a controlled environment we eliminate the antibiotics, hormones, and contamination risks inherent in the traditional industry. Safer for your dog. Better for everyone." },
  { letter: "A", title: "All-Animal Advocacy", img: DOG_OWNER, desc: "We believe a dog's love should not cost another life. This is the only logical choice for the modern, conscious pet parent. ETHIA exists because every animal matters." },
];

const PillarCard = ({ pillar, index }) => {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} className={`sticky top-24 transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{ zIndex: index + 1 }}>
      <div className="rounded-2xl p-6 md:p-10 mb-6 shadow-lg" style={{ background: C.white }}>
        <div className="grid grid-cols-1 md:grid-cols-[0.8fr_1fr_1.5fr] gap-6 items-center">
          <div>
            <span className="text-6xl md:text-7xl font-black" style={{ color: `${C.maroon}15`, fontFamily: "Fraunces, serif" }}>{pillar.letter}</span>
            <h3 className="text-lg md:text-xl font-bold" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>{pillar.title}</h3>
          </div>
          <div className="flex justify-center">
            <img src={pillar.img} alt={pillar.title} className="w-full max-w-[220px] h-40 object-cover rounded-xl shadow-md" />
          </div>
          <p className="text-sm leading-relaxed" style={{ color: `${C.maroon}99` }}>{pillar.desc}</p>
        </div>
      </div>
    </div>
  );
};

const FivePillars = () => (
  <section className="py-16 md:py-24" style={{ background: C.beige }} data-testid="five-pillars">
    <div className="max-w-[1100px] mx-auto px-6 md:px-[52px]">
      <div className="text-center mb-4"><span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: C.maroon }}>THE FIVE PILLARS OF ETHIA</span></div>
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-14" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>E.T.H.I.A.</h2>
      <div className="space-y-0">{PILLARS.map((p, i) => <PillarCard key={i} pillar={p} index={i} />)}</div>
    </div>
  </section>
);

/* ─── Mission ─── */
const MissionSection = () => {
  const [ref, vis] = useReveal();
  return (
    <section ref={ref} className="py-16 md:py-24" style={{ background: C.maroonDark }} data-testid="mission-section">
      <div className={`max-w-[1100px] mx-auto px-6 md:px-[52px] transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-[0.4fr_1fr] gap-8 lg:gap-16">
          <div>
            <span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: C.yellow }}>Mission.</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-3" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>Our Why.</h2>
          </div>
          <p className="text-sm md:text-base leading-relaxed" style={{ color: `${C.beige}CC` }}>
            For decades, the pet food industry has been built on systems that compromise quality, obscure the truth, and animal farming catered to pet food, not only harming the animals but also the planet. We build a bridge to be part of a system that is based on ethical and sustainable innovation for pet nutrition that is complete. ETHIA brings the next generation of pet food to India: cell-based food that is ethical, clean, safe, real, and nutritionally uncompromising. Our signature product is made using cultured chicken cells (real chicken cells) in a plant-based matrix derived from natural ingredients only. This is not imitation meat. And it is not a trend. It is the future of feeding — built from science, responsibility, and compassion.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ─── Blog / Medium ─── */
const BLOGS = [
  { emoji: "🧬", cat: "Science", title: "Cultured Chicken Cells: The Future of Dog Food", excerpt: "Understanding the technology behind lab-grown protein.", time: "5 min", url: "https://medium.com/@ethia_36696/cultured-chicken-cells-the-future-of-dog-food-74717a81994e" },
  { emoji: "🐕", cat: "Nutrition", title: "Pet Food Redefined", excerpt: "The nutritional science behind complete pet food.", time: "4 min", url: "https://medium.com/@ethia_36696/pet-food-redefined-0749b6dc3dff" },
  { emoji: "🌱", cat: "Sustainability", title: "Understanding Clean Label Pet Food", excerpt: "How cultivated meat reduces our carbon pawprint.", time: "6 min", url: "https://medium.com/@ethia_36696/understanding-clean-label-pet-food-5f8573267c6f" },
  { emoji: "🔬", cat: "Innovation", title: "Why Dogs Need Real Protein", excerpt: "A deep dive into protein requirements for dogs.", time: "7 min", url: "https://medium.com/@ethia_36696/why-dogs-need-real-protein-3ff4d76c4fb2" },
  { emoji: "🌍", cat: "Ethics", title: "The Environmental Impact", excerpt: "Why slaughter-free matters for the planet.", time: "4 min", url: "https://medium.com/@ethia_36696/the-environmental-impact-b159c93e2311" },
  { emoji: "❤️", cat: "Ethics", title: "Zero Cruelty Pet Food", excerpt: "Industry trends and what they mean for your dog.", time: "5 min", url: "https://medium.com/@ethia_36696" },
];

const BlogSection = () => (
  <section className="py-16 md:py-24" style={{ background: C.beige }} data-testid="blog-section">
    <div className="max-w-[1100px] mx-auto px-6 md:px-[52px]">
      <div className="text-center mb-4"><span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: C.maroon }}>BLOG AND RESOURCES</span></div>
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-3" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>Learn About Cultivated Pet Food</h2>
      <p className="text-center text-sm md:text-base mb-12" style={{ color: `${C.maroon}99` }}>Expert insights and stories on Medium.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {BLOGS.map((b, i) => (
          <a key={i} href={b.url} target="_blank" rel="noopener noreferrer" className="rounded-2xl p-6 shadow-md hover:-translate-y-1 transition-all duration-300 block" style={{ background: C.white }} data-testid={`blog-card-${i}`}>
            <span className="text-2xl">{b.emoji}</span>
            <span className="inline-block ml-2 px-3 py-1 rounded-full text-[10px] font-bold" style={{ background: `${C.maroon}10`, color: C.maroon }}>{b.cat}</span>
            <h3 className="text-base font-bold mt-3 mb-2" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>{b.title}</h3>
            <p className="text-xs mb-3" style={{ color: `${C.maroon}99` }}>{b.excerpt}</p>
            <span className="text-[10px] font-medium" style={{ color: `${C.maroon}60` }}>{b.time} read</span>
          </a>
        ))}
      </div>
      <div className="text-center mt-10">
        <a href="https://medium.com/@ethia_36696" target="_blank" rel="noopener noreferrer" className="inline-block font-bold px-8 py-3.5 rounded-full text-sm transition-all hover:scale-105" style={{ background: C.yellow, color: C.maroon }} data-testid="blog-medium-btn">View All on Medium <ArrowRight className="inline ml-1" size={16} /></a>
      </div>
    </div>
  </section>
);

/* ─── Contact Info Cards ─── */
const ContactCards = () => {
  const cards = [
    { icon: <Phone size={24} />, label: "Phone", value: "+91 93279 27782", sub: "Mon-Sat 9AM-6PM" },
    { icon: <Mail size={24} />, label: "Email", value: "admin@ethia.in", sub: "24/7 Support" },
    { icon: <MapPin size={24} />, label: "Location", value: "Mumbai, Maharashtra", sub: "India" },
    { icon: <Clock size={24} />, label: "Hours", value: "Monday to Saturday", sub: "9:00 AM to 6:00 PM" },
  ];
  return (
    <section className="py-16 md:py-24" style={{ background: C.beige }} data-testid="about-contact-info">
      <div className="max-w-[1100px] mx-auto px-6 md:px-[52px]">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {cards.map((c, i) => (
            <div key={i} className="rounded-2xl p-5 md:p-6 text-center hover:-translate-y-1 transition-all duration-300" style={{ background: C.maroon }}>
              <div className="flex justify-center mb-3" style={{ color: C.yellow }}>{c.icon}</div>
              <div className="font-bold text-xs md:text-sm mb-1" style={{ color: C.yellow }}>{c.label}</div>
              <div className="font-semibold text-xs md:text-sm mb-1 break-words" style={{ color: C.white }}>{c.value}</div>
              <div className="text-[10px] md:text-xs" style={{ color: `${C.white}99` }}>{c.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Final CTA ─── */
const FinalCTA = () => (
  <section className="py-16 md:py-20" style={{ background: C.beige }} data-testid="about-final-cta">
    <div className="max-w-[800px] mx-auto px-6">
      <div className="rounded-2xl md:rounded-3xl p-8 md:p-14 text-center" style={{ background: C.maroonDark }}>
        <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>Be First. Feed Better. Save Lives. &#128062;</h3>
        <p className="text-sm md:text-base mb-8" style={{ color: `${C.beige}CC` }}>Be the first in India to feed your dog food that saves lives.</p>
        <a href="/products" className="inline-block font-bold px-10 py-4 rounded-full transition-all hover:scale-105 text-sm md:text-base" style={{ background: C.yellow, color: C.maroon }}>Order Now <ArrowRight className="inline ml-1" size={18} /></a>
      </div>
    </div>
  </section>
);

/* ─── Page ─── */
const AboutPage = () => {
  useEffect(() => { document.title = "ETHIA | About Us"; }, []);
  return (
    <div className="min-h-screen" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="about-page">
      <MaroonNavbar />
      <OurStorySection />
      <FivePillars />
      <MissionSection />
      <BlogSection />
      <ContactCards />
      <FinalCTA />
      <MaroonFooter />
      <WhatsAppButton />
    </div>
  );
};

export default AboutPage;
